import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Fab from "@mui/material/Fab";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { ExtraWorkAggreementApprovalStatusResponse, GetProjectResponse, useGetApiProjectsByProjectIdExtraworkgreementsQuery } from "api/generatedApi";
import { useScreenSize, ScreenSizeEnum } from "shared/use-screen-size";
import { DefaultBack2DashBoardNavigation } from "../navigation/default";
import { useToast } from "shared/toast/hooks/use-toast";
import { useDashboardRestrictions } from "shared/user-restrictions/use-dashboard-restrictions";
import { MobileProjectExtraWorkAgreements } from "./mobile-table/mobile-table";
import { DesktopProjectExtraWorkAgreements } from "./desktop-table/desktop-table";
import { ValueCard } from "components/shared/card/value-card";
import { useExtraWorkAgreementFormatter } from "./hooks/format";
import { useNavigate } from "react-router-dom";

interface Props {
  project: GetProjectResponse;
}

export const DefaultProjectExtraWorkAgreements = (props: Props) => {
  const { project } = props;
  const { screenSize } = useScreenSize();
  const { t } = useTranslation();
  const tRef = useRef(t);
  const toast = useToast();
  const toastRef = useRef(toast);
  const { data, error, isLoading } = useGetApiProjectsByProjectIdExtraworkgreementsQuery({ projectId: project?.id ?? "" });
  const { canCreateFavorites } = useDashboardRestrictions(project);
  const [selectedStatus, setSelectedStatus] = useState<ExtraWorkAggreementApprovalStatusResponse | "">("");
  const { getStatusFormatted } = useExtraWorkAgreementFormatter();
  const navigate = useNavigate();

  useEffect(() => {
    if (error) {
      if ("status" in error && error.status === 401) {
        return toastRef.current.error(tRef.current("dashboard.extraWorkAgreements.getExtraWorkAgreements.errorUnauthorized"));
      }
      toastRef.current.error(tRef.current("dashboard.extraWorkAgreements.getExtraWorkAgreements.error"));
    }
  }, [data, error, toastRef, tRef]);

  const onClickCreate = () => {
    navigate(`/projects/${project.id}/dashboard/extraworkagreements/create`);
  };

  return (
    <Grid item xs={true} overflow={"auto"} padding={1} height={"100%"}>
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "start" }}>
        <DefaultBack2DashBoardNavigation here={t("dashboard.links.extraWorkAgreements")} />
        {isLoading && (
          <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <CircularProgress size={100} />
          </Box>
        )}
        {!isLoading && (!data?.extraWorkAgreements || data.extraWorkAgreements.length <= 0) && (
          <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Typography variant="body1" color="grey.100" fontStyle={"italic"}>
              {t("dashboard.extraWorkAgreements.getExtraWorkAgreements.noExtraWorkAgreements")}
            </Typography>
          </Box>
        )}

        {/* approved workagreement totalpayment box and  extra workagreement summary status payment */}
        {!isLoading && data?.extraWorkAgreements && data.extraWorkAgreements.length > 0 && (
          <Box
            sx={{
              pt: 5,
              px: screenSize === ScreenSizeEnum.Mobile ? 0 : 4,
              display: "flex",
              gap: 4,
              flexDirection: screenSize === ScreenSizeEnum.Mobile ? "column" : "row",
            }}
          >
            <ValueCard titleNamespace="dashboard.extraWorkAgreements.summaries.totalPayment" value={data?.totalPaymentDkr} unitNamespace={"common.currency"} asCurrency={true} showBackground={false} fontSize={"large"} />

            <ValueCard
              titleNamespace={t("dashboard.extraWorkAgreements.summaries.forSelectedType", {
                status: getStatusFormatted(selectedStatus || "AwaitingApproval"),
              })}
              value={data.paymentDkrByStatus![selectedStatus || "AwaitingApproval"] ?? 0}
              unitNamespace={"common.currency"}
              asCurrency={true}
              showBackground={false}
              fontSize={"large"}
            />
          </Box>
        )}

        {/* mobile or desktop view for extra work agreement list */}
        {!isLoading &&
          data?.extraWorkAgreements &&
          data.extraWorkAgreements.length > 0 &&
          (screenSize === ScreenSizeEnum.Mobile ? (
            <MobileProjectExtraWorkAgreements project={project} agreements={data.extraWorkAgreements} selectedStatus={selectedStatus} setSelectedStatus={setSelectedStatus} />
          ) : (
            <DesktopProjectExtraWorkAgreements project={project} agreements={data.extraWorkAgreements} selectedStatus={selectedStatus} setSelectedStatus={setSelectedStatus} />
          ))}

        {!isLoading && canCreateFavorites() && (
          <Fab
            sx={{
              position: "absolute",
              bottom: screenSize === ScreenSizeEnum.Mobile ? "70px" : "30px",
              right: screenSize === ScreenSizeEnum.Mobile ? "20px" : "10px",
              backgroundColor: "primary.dark",
            }}
            onClick={onClickCreate}
          >
            <AddCircleOutlineIcon fontSize="large" sx={{ color: "grey.100", pb: "2px", pr: "2px" }} />
          </Fab>
        )}
      </Box>
    </Grid>
  );
};
